/**
 * The RouteSummary view allow to display a additional route information.
 *
 * The tomtom.routeSummary() method creates a new instance of RouteSummary view.
 *
 * @module Widgets
 * @class routeSummary
 *
 */
define('widgets/views/routeSummary',['display/map', 'unitFormatConverter/unitFormatConverter'], function(L, unitFormatConverter) {
    'use strict';

    function initPanelResult(className, control) {
        var divPanelRouteResultWrapper = L.DomUtil.create('div', className + '-route-result-wrapper', control.divPanelRouteResult);
        control.divPanelRouteResult = L.DomUtil.create('div', className + '-route-result', control._container);
        control.divPanelRouteDeparture = L.DomUtil.create('div', className + '-leave', control.divPanelRouteResult);
        control.divPanelRouteDeparture.innerHTML = 'Leave: now';
        control.divPanelRouteCostModel = L.DomUtil.create('div', className + '-route-cost-model', divPanelRouteResultWrapper);
        control.divPanelRouteTime = L.DomUtil.create('div', className + '-route-time', divPanelRouteResultWrapper);
        control.divPanelRouteTrafficDelay = L.DomUtil.create('div', className + '-route-traffic-delay', divPanelRouteResultWrapper);
    }

    function createPanelArriveDistance(className, control, parent) {
        var divPanelRouteArriveDistance = L.DomUtil.create('div', className + '-route-arrive-row', parent);
        var divPanelRouteArriveDistanceLabel = L.DomUtil.create('div', className + '-route-arrive-cell-left', divPanelRouteArriveDistance);
        divPanelRouteArriveDistanceLabel.innerHTML = 'Distance:';
        control.divPanelRouteArriveDistanceValue = L.DomUtil.create('div', className + '-route-arrive-cell-right', divPanelRouteArriveDistance);
    }

    function createPanelArriveTime(className, control, parent) {
        var divPanelRouteArriveTime = L.DomUtil.create('div', className + '-route-arrive-row', parent);
        var divPanelRouteArriveTimeLabel = L.DomUtil.create('div', className + '-route-arrive-cell-left', divPanelRouteArriveTime);
        divPanelRouteArriveTimeLabel.innerHTML = 'Arrive:';
        control.divPanelRouteArriveTimeValue = L.DomUtil.create('div', className + '-route-arrive-cell-right', divPanelRouteArriveTime);
    }

    function initPanelArrive(className, control) {
        var divPanelRouteArrive = L.DomUtil.create('div', className + '-arrive', control._container);
        createPanelArriveDistance(className, control, divPanelRouteArrive);
        createPanelArriveTime(className, control, divPanelRouteArrive);
    }

    function createDOMElements(control) {
        var className = 'leaflet-control-route-summary',
            container = L.DomUtil.create('div', className + ' leaflet-control'),
            divHeading = L.DomUtil.create('div', className + '-heading', container);
        container.style.width = control.options.size[0] + 'px';
        container.style.height = control.options.size[1] + 'px';
        divHeading.innerHTML = 'Route summary';
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);
        control._container = container;
        initPanelResult(className, control);
        initPanelArrive(className, control);
        container.style.visibility = 'hidden';

        return container;
    }

    /**
     * The method to instantiates the view. The options can be passed on creation.
     *
     * @method routeSummary
     * @param {Object} [options] Options to setup control.
     * @param {String} [options.size] Size of panel.
     * @param {String} [options.position] Where to add view.
     * @param {Boolean} [options.imperialDistance] Should the distance be displayed in imperial units.
     *
     * @example
     * ```javascript
     *
     * var options = {
     *       size: [270, 268],
     *       position: "topleft"
     *   };
     * var routeSummaryInstance = routeSummary(options).addTo(map);
     *
     * ```
     */
    return function(options) {

        var RouteSummary = L.Control.extend({

            options: {
                size: [320, 320],
                position: 'topleft',
                imperialDistance: false
            },

            /**
             * Leaflet init method executed when adding control to map.
             * Options will be stored inside control.
             *
             * @method initialize
             * @param {Object} [options] Options to setup control.
             *
             */
            initialize: function(options) {
                L.setOptions(this, options);
                this._attributions = {};
            },

            /**
             * Leaflet method executed when adding control to map.
             * Map will be stored inside control.
             *
             * @method onAdd
             * @param {Object} [map] The map instance to which control is added.
             *
             */
            onAdd: function(map) {
                this._map = map;
                return createDOMElements(this);
            },

            /**
             * Method to hide route summary.
             * By default summary will be hidden.
             *
             * @method hide
             *
             */
            hide: function() {
                this._container.style.visibility = 'hidden';
            },

            /**
             * Method to show route summary.
             * By default summary will be hidden.
             * Use this method to show control after data is ready.
             *
             * @method show
             *
             */
            show: function() {
                this._container.style.visibility = '';
            },

            /**
             * Method to update data after route is recalculated.
             * Use this method to update data after each route calculation.
             *
             * @method updateSummaryData
             * @param {Object} data The routing properties object containing fields: _travelTimeInSeconds,
             *     trafficDelayInSeconds, lengthInMeters, arrivalTime_.
             * @param {Boolean} [imperial] Display distance in imperial units.
             *
             */
            updateSummaryData: function(data, imperial) {
                this.divPanelRouteTime.innerHTML = unitFormatConverter.formatTime(data.travelTimeInSeconds);
                this.divPanelRouteTrafficDelay.innerHTML = unitFormatConverter.formatTime(data.trafficDelayInSeconds) + ' traffic';
                this.divPanelRouteArriveDistanceValue.innerHTML = unitFormatConverter.formatDistance(data.lengthInMeters, imperial || this.options.imperialDistance);
                this.divPanelRouteArriveTimeValue.innerHTML = unitFormatConverter.formatHourAndMinute(data.arrivalTime);
                this.show();
            }
        });

        return new RouteSummary(options);
    };
});

